import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Label } from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { moveItemWithinSubset } from 'services/arrays/moveItem';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { fetchInstallationCategories } from 'store/products/actions';
import { WRITE_OFFERS } from 'services/enums/users/permissions';
import useHasPermission from 'services/users/useHasPermission';
import {
  currencyOfCountry,
  currencySymbol,
} from 'services/offers/exchangeRate';

import FetchPriceListTemplate from './FetchPriceListTemplate';
import OfferProductsTable from './OfferProductsTable';
import OfferTextField from './OfferTextField';
import SavePriceListTemplate from './SavePriceListTemplate';
import SearchPositionModal from './SearchPositionModal';

import 'style/offers/OfferPriceList.css';

const ADDITIONAL_OFFER = 'additional-offer';

/**
 * The additional price list of the offer: rows calculated like positions (materials, work,
 * freight), printed on a page of their own after the offer by their name and unit price.
 * A row can have a price text instead (e.g. "25 %"); a row with neither is a note.
 */
const OfferPriceList = ({
  country,
  locked,
  offerId,
  products,
  setProducts,
  setShowQuantities,
  setText,
  showQuantities,
  text,
  type,
}: {
  // The country of the department of the offer: its prices are in SEK in Sweden, else €
  country?: null | string;
  locked: boolean;
  offerId: number;
  // All rows of the offer (positions and price list rows), null until loaded
  products: null | IOfferProductsGet[];
  setProducts: (products: IOfferProductsGet[]) => void;
  // Kept by the offer page, as this section is unmounted while closed
  setShowQuantities: (showQuantities: boolean) => void;
  // The quantities are printed in the price list of the PDF
  showQuantities: boolean;
  // The saved text at the bottom of the price list, kept by the offer page as well
  setText: (text: string) => void;
  text: string;
  type: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation([translations.offers, translations.common]);

  const [reordering, setReordering] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [fetchOpen, setFetchOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const canWriteOffers = useHasPermission(WRITE_OFFERS);
  // The text being edited, saved with its own button
  const [draftText, setDraftText] = useState(text);
  const [savingText, setSavingText] = useState(false);
  useEffect(() => setDraftText(text), [text]);

  // The rows are shared with the positions section: a failed load leaves them unloaded
  // (not empty), so the positions section loads them itself
  const loadProducts = async () => {
    try {
      setProducts(
        type === ADDITIONAL_OFFER
          ? await apiOffers.products.getForAdditionalOffer(offerId)
          : await apiOffers.products.getForOffer(offerId)
      );
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
    }
  };

  // Needed to count only installations (not other expenses) in the hours
  useEffect(() => {
    dispatch(fetchInstallationCategories());
  }, [dispatch]);

  useEffect(() => {
    if (products === null) {
      loadProducts();
    }
  }, []);

  const rows =
    products === null
      ? []
      : products.filter((product) => product.isPriceListItem === true);

  const canReorder = !locked && rows.length > 1;

  // Moves a row within its work type and saves the order at once (as in the PDF)
  const moveRow = async (group: string, fromIndex: number, toIndex: number) => {
    if (products === null) {
      return;
    }

    const reordered = moveItemWithinSubset(
      products,
      (product) => product.group === group && product.isPriceListItem === true,
      fromIndex,
      toIndex
    );
    setProducts(reordered);

    try {
      const ids = reordered
        .filter((product) => product.isPriceListItem === true)
        .map((product) => product.id);

      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.positionOrder.put(offerId, ids);
      } else {
        await apiOffers.positionOrder.put(offerId, ids);
      }
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:Saving position order failed`))
      );
      loadProducts();
    }
  };

  // A row removed with one click, e.g. what is not needed of a master list fetched to
  // the offer
  const removeRow = async (id: number) => {
    if (products === null) {
      return;
    }

    setProducts(products.filter((product) => product.id !== id));

    try {
      await apiOffers.products.delete(id);
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:priceList.Delete failed`)));
      loadProducts();
    }
  };

  const changeShowQuantities = async (value: boolean) => {
    setSavingSettings(true);
    setShowQuantities(value);

    try {
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.patchPriceListSettings(offerId, {
          showQuantities: value,
        });
      } else {
        await apiOffers.patchPriceListSettings(offerId, {
          showQuantities: value,
        });
      }
    } catch (error) {
      setShowQuantities(!value);
      dispatch(addMessage(t(`${translations.offers}:priceList.Save failed`)));
    } finally {
      setSavingSettings(false);
    }
  };

  const saveText = async () => {
    setSavingText(true);

    try {
      const settings = { setText: true, text: draftText };
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.patchPriceListSettings(
          offerId,
          settings
        );
      } else {
        await apiOffers.patchPriceListSettings(offerId, settings);
      }
      setText(draftText);
      dispatch(addMessage(t(`${translations.offers}:priceList.Text saved`)));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:priceList.Save failed`)));
    } finally {
      setSavingText(false);
    }
  };

  return (
    <div className="offer-price-list">
      <p className="offer-price-list-info">
        {t(`${translations.offers}:priceList.Info`)}
      </p>
      <div className="offer-price-list-toolbar">
        <Label check className="offer-price-list-checkbox">
          <Input
            type="checkbox"
            checked={showQuantities}
            disabled={locked || savingSettings}
            onChange={(event) => changeShowQuantities(event.target.checked)}
          />
          {t(`${translations.offers}:priceList.Show quantities`)}
        </Label>
        {/* A list of the registry fetched to the offer, after the rows it has */}
        {locked ? null : (
          <PrimaryButton onClick={() => setFetchOpen(true)} size="sm">
            {t(`${translations.offers}:priceListRegistry.Fetch button`)}
          </PrimaryButton>
        )}
        {/* One row from the price lists saved on the offers, as the position search */}
        {locked ? null : (
          <PrimaryButton onClick={() => setSearchOpen(true)} size="sm">
            {t(`${translations.offers}:priceList.Search row`)}
          </PrimaryButton>
        )}
        {/* The rows to the registry, from which a department's new offers can get them */}
        {rows.length > 0 && canWriteOffers ? (
          <PrimaryButton onClick={() => setRegistryOpen(true)} size="sm">
            {t(`${translations.offers}:priceListRegistry.Button`)}
          </PrimaryButton>
        ) : null}
        {canReorder ? (
          <PrimaryButton
            className={reordering ? 'position-order-active' : undefined}
            onClick={() => setReordering(!reordering)}
            size="sm"
          >
            {reordering
              ? t(`${translations.offers}:Position order done`)
              : t(`${translations.offers}:priceList.Change order`)}
          </PrimaryButton>
        ) : null}
      </div>
      {loadFailed ? (
        <p className="offer-price-list-empty">
          {t(`${translations.offers}:priceList.Load failed`)}
        </p>
      ) : null}
      {products === null ? null : (
        <>
          {rows.length === 0 ? (
            <p className="offer-price-list-empty">
              {t(`${translations.offers}:priceList.No rows`)}
            </p>
          ) : (
            <OfferProductsTable
              money={{
                convert: (value) => value,
                symbol: currencySymbol(currencyOfCountry(country)),
              }}
              move={moveRow}
              priceList
              products={rows}
              remove={locked ? undefined : removeRow}
              reordering={reordering && canReorder}
              type={type}
            />
          )}
        </>
      )}
      {locked ? null : (
        <div className="offer-price-list-actions">
          <PrimaryButton
            className="button-one-line"
            onClick={() =>
              history.push(
                `/offers/${offerId}/${type}/products/create?priceList=true`
              )
            }
          >
            {t(`${translations.offers}:priceList.Add row`).toLocaleUpperCase()}
          </PrimaryButton>
        </div>
      )}
      {/* The text below the rows in the PDF, with the same formatting and archive as the
          offer text */}
      <div className="offer-price-list-text">
        <OfferTextField
          label={t(`${translations.offers}:priceList.Text`)}
          locked={locked}
          onChange={setDraftText}
          textType="price-list"
          value={draftText}
        />
        {locked ? null : (
          <PrimaryButton
            disabled={savingText || draftText === text}
            onClick={saveText}
            size="sm"
          >
            {t(`${translations.offers}:priceList.Save text`)}
          </PrimaryButton>
        )}
      </div>
      <FetchPriceListTemplate
        additionalOffer={type === ADDITIONAL_OFFER}
        isOpen={fetchOpen}
        offerId={offerId}
        onFetched={loadProducts}
        rowCount={rows.length}
        toggle={() => setFetchOpen(!fetchOpen)}
      />
      {locked ? null : (
        <SearchPositionModal
          isOpen={searchOpen}
          offerId={offerId}
          onCopied={loadProducts}
          priceList
          toggle={() => setSearchOpen(!searchOpen)}
          type={type}
        />
      )}
      <SavePriceListTemplate
        additionalOffer={type === ADDITIONAL_OFFER}
        isOpen={registryOpen}
        offerId={offerId}
        toggle={() => setRegistryOpen(!registryOpen)}
      />
    </div>
  );
};

export default OfferPriceList;
